import { ReactElement } from "react";
import { IconButton, Tooltip, SxProps, Theme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

interface DeleteIconButtonProps {
  /**
   * Callback when delete button is clicked
   */
  onClick: () => void;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Tooltip message to display when disabled
   * If not provided, shows default "Delete" message when enabled
   */
  disabledTooltip?: string;
  /**
   * Tooltip message when enabled (overrides default)
   * @default "Delete"
   */
  tooltip?: string;
  /**
   * Custom sx styles
   */
  sx?: SxProps<Theme>;
  /**
   * Size of the icon button
   * @default "small"
   */
  size?: "small" | "medium" | "large";
}

/**
 * Reusable Delete Icon Button Component
 *
 * Displays a delete icon button with optional tooltip and disabled state.
 * Used consistently across the application for delete actions.
 *
 * @example
 * // Basic usage
 * <DeleteIconButton
 *   onClick={() => handleDelete(item.id)}
 * />
 *
 * @example
 * // With disabled state and tooltip
 * <DeleteIconButton
 *   onClick={() => handleDelete(item.id)}
 *   disabled={isInUse}
 *   disabledTooltip="Cannot delete: This item is in use"
 * />
 *
 * @example
 * // Custom styling
 * <DeleteIconButton
 *   onClick={handleDelete}
 *   sx={{ color: "error.main" }}
 *   size="medium"
 * />
 */
function DeleteIconButton({
  onClick,
  disabled = false,
  disabledTooltip,
  tooltip = "Delete",
  sx,
  size = "small",
}: DeleteIconButtonProps): ReactElement {
  const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : tooltip;

  return (
    <Tooltip title={tooltipTitle} arrow>
      <span>
        <IconButton
          size={size}
          onClick={onClick}
          disabled={disabled}
          color="error"
          sx={sx}
        >
          <DeleteIcon fontSize={size} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default DeleteIconButton;
